import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";

export default function BirthdayRemindersPage() {
  const { http } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [items, setItems] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [customWishText, setCustomWishText] = useState(
    "Happy Birthday {Name}! 🎂🎉 Wishing you a fantastic year filled with health, joy, and success! Best regards from RTO CRM."
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get("/api/admin/birthdays/today");
      setItems(res.data.items || []);
      if (res.data.currentYear) setCurrentYear(res.data.currentYear);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load today's birthdays.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const total = items.length;
    const sent = items.filter((i) => i.whatsappStatus === "SENT").length;
    const failed = items.filter((i) => i.whatsappStatus === "FAILED").length;
    const pending = total - sent - failed;
    return { total, sent, failed, pending };
  }, [items]);

  const sendReminder = async (customerId = null, customerName = "") => {
    if (customerId) setSendingId(customerId);
    else setSendingAll(true);

    setError(null);
    setSuccess(null);
    try {
      const wishText = customWishText.replace("{Name}", customerName || "");
      const res = await http.post("/api/admin/birthdays/send-reminders", {
        customerId,
        customText: wishText,
      });

      const count = res.data.results?.length || 0;
      const isSimulated = res.data.results?.some((r) => r.simulated);
      setSuccess(
        `Successfully processed ${count} WhatsApp wish(es).${
          isSimulated ? " (Test Mode: Message simulated)" : ""
        }`
      );
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send WhatsApp reminder.");
    } finally {
      setSendingId(null);
      setSendingAll(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "SENT") {
      return <span className="badge text-bg-success">SENT 🟢</span>;
    }
    if (status === "FAILED") {
      return <span className="badge text-bg-danger">FAILED 🔴</span>;
    }
    return <span className="badge text-bg-warning text-dark">PENDING 🟡</span>;
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1">Customer Birthday Reminders 🎂</h4>
          <p className="text-muted mb-0">
            Automatically detect customer birthdays today and send warm wishes via WhatsApp Business API ({currentYear}).
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
            Refresh
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={() => sendReminder(null)}
            disabled={sendingAll || !stats.pending || loading}
          >
            {sendingAll ? "Sending All..." : "🚀 Send Wishes to All Pending"}
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-primary border-4">
            <div className="card-body">
              <div className="text-muted small">Today's Birthdays</div>
              <div className="fs-3 fw-bold">{stats.total}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-success border-4">
            <div className="card-body">
              <div className="text-muted small">WhatsApp Sent</div>
              <div className="fs-3 fw-bold text-success">{stats.sent}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-warning border-4">
            <div className="card-body">
              <div className="text-muted small">Pending Wishes</div>
              <div className="fs-3 fw-bold text-warning">{stats.pending}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-danger border-4">
            <div className="card-body">
              <div className="text-muted small">Delivery Failed</div>
              <div className="fs-3 fw-bold text-danger">{stats.failed}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h6 className="mb-2">WhatsApp Message Template</h6>
          <p className="text-muted small mb-2">
            Customize the birthday wish message below. <code>{"{Name}"}</code> will automatically be replaced with each customer's full name.
          </p>
          <SpeechInput
            as="textarea"
            name="customWishText"
            value={customWishText}
            onChange={(e) => setCustomWishText(e.target.value)}
            rows={2}
            placeholder="Type birthday message template"
          />
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Today's Birthday Customer List</h6>
            <span className="text-muted small">
              {loading ? "Loading..." : `${items.length} customer(s)`}
            </span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Customer Name</th>
                  <th>Mobile No</th>
                  <th>Email</th>
                  <th>Birth Date</th>
                  <th>Age</th>
                  <th>WhatsApp Status</th>
                  <th>Sent At</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      Checking for today's birthdays...
                    </td>
                  </tr>
                ) : null}

                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      🎉 No customer birthdays today!
                    </td>
                  </tr>
                ) : null}

                {!loading &&
                  items.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold text-uppercase">{item.fullName}</td>
                      <td>
                        <a href={`tel:${item.mobile}`} className="text-decoration-none">
                          📱 {item.mobile}
                        </a>
                      </td>
                      <td>{item.email || "-"}</td>
                      <td>{item.birthDate}</td>
                      <td>
                        <span className="badge text-bg-light border">{item.age} yrs</span>
                      </td>
                      <td>{getStatusBadge(item.whatsappStatus)}</td>
                      <td className="small text-muted">
                        {item.whatsappSentAt
                          ? new Date(item.whatsappSentAt).toLocaleString()
                          : "-"}
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => sendReminder(item.id, item.fullName)}
                          disabled={sendingId === item.id || sendingAll}
                        >
                          {sendingId === item.id ? "Sending..." : "💬 Send WhatsApp Wish"}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
